import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CalendarPlus,
  Trash2,
  AlertTriangle,
  Save,
  Search,
  Check,
  ChevronDown,
  X as XIcon,
  Pencil,
} from 'lucide-react';
import FormDrawer from '../FormDrawer';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import {
  displayName,
  formatDate,
  lifecycleLabel,
  lifecyclePillClass,
} from './utilizationHelpers';

const EMPTY_FORM = {
  project_id: '',
  phase: '',
  start_date: '',
  end_date: '',
  allocation_percent: 100,
  is_billable: null,
};

const toInputDate = (value) => {
  if (!value) return '';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (_) {
    return '';
  }
};

const toIsoAtMidnight = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/**
 * Searchable single-select dropdown for projects.
 *
 * Mirrors the style of the main workspace filter popovers: trigger button
 * that opens a floating card with a search input and a scrollable options
 * list, each row showing a check indicator when selected.
 */
const ProjectCombobox = ({
  value,
  onChange,
  projects = [],
  disabled = false,
  emptyLabel = 'No projects available for this employee',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const selected = useMemo(
    () => projects.find((p) => p.id === value) || null,
    [projects, value],
  );

  const q = query.trim().toLowerCase();
  const filtered = q
    ? projects.filter((p) => {
        const name = (p.name || '').toLowerCase();
        const client = (p.client || '').toLowerCase();
        return name.includes(q) || client.includes(q);
      })
    : projects;

  const triggerLabel = selected
    ? `${selected.name}${selected.client ? ` (${selected.client})` : ''}`
    : projects.length === 0
      ? emptyLabel
      : 'Select a project...';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled || projects.length === 0}
        onClick={() => setOpen((o) => !o)}
        className="input-field w-full text-left flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="allocation-project"
        data-value={value || ''}
      >
        <span
          className={
            selected
              ? 'truncate'
              : 'truncate text-secondary-400 dark:text-secondary-500'
          }
        >
          {triggerLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          data-testid="allocation-project-popover"
          className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden flex flex-col rounded-lg border border-secondary-200 bg-white shadow-lg dark:bg-secondary-800 dark:border-secondary-700"
        >
          <div className="p-2 border-b border-secondary-200 dark:border-secondary-700 shrink-0">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="input-field w-full pl-8 py-1.5 text-sm"
                data-testid="allocation-project-search"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">
                Select a project
              </span>
              {value && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0 p-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-secondary-500 px-2 py-3">
                {projects.length === 0
                  ? emptyLabel
                  : `No projects match "${query}"`}
              </p>
            ) : (
              filtered.map((p) => {
                const isSelected = p.id === value;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      onChange(p.id);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded text-left text-sm hover:bg-secondary-50 dark:hover:bg-secondary-700/50 ${
                      isSelected
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                        : 'text-secondary-900 dark:text-secondary-100'
                    }`}
                    data-testid={`allocation-project-option-${p.id}`}
                  >
                    <span className="flex-1 min-w-0 truncate">
                      {p.name}
                      {p.client ? (
                        <span className="ml-1 text-xs text-secondary-500">
                          ({p.client})
                        </span>
                      ) : null}
                      {p.is_billable === false ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                          Non-billable
                        </span>
                      ) : null}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary-600 shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AllocationDrawer = ({
  open,
  onClose,
  user,
  projects = [],
  allocations: initialAllocations = [],
  initialProjectId = null,
  onSaved,
}) => {
  const [allocations, setAllocations] = useState(initialAllocations);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflictReport, setConflictReport] = useState(null);

  // Scope the project dropdown to projects this employee is actually a member
  // of. We prefer the authoritative ``project.member_ids`` list surfaced by the
  // backend, and fall back to the user's own project links so the list is
  // never empty for in-flight or legacy records.
  const scopedProjects = useMemo(() => {
    if (!user) return [];
    const userId = String(user.id || user._id || '');
    const fallbackIds = new Set(
      [
        ...(user.per_project || []).map((p) => p?.project_id),
        ...(user.current_projects || []).map((p) => p?.project_id),
        ...(user.allocations || []).map((a) => a?.project_id),
      ]
        .filter(Boolean)
        .map(String),
    );
    const scoped = projects.filter((p) => {
      const memberIds = (p.member_ids || []).map(String);
      if (memberIds.length) return memberIds.includes(userId);
      return fallbackIds.has(String(p.id));
    });
    // When editing, keep the project in view even if the membership has
    // changed since the allocation was created so the user can still see it.
    if (editingId && form.project_id) {
      const already = scoped.some((p) => p.id === form.project_id);
      if (!already) {
        const fallback = projects.find((p) => p.id === form.project_id);
        if (fallback) return [...scoped, fallback];
      }
    }
    return scoped;
  }, [projects, user, editingId, form.project_id]);

  useEffect(() => {
    if (open) {
      setAllocations(initialAllocations);
      setConflictReport(null);
      setEditingId(null);
      setForm((f) => ({
        ...EMPTY_FORM,
        project_id: initialProjectId || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, initialProjectId]);

  // Live conflict preview: synthesize the "about to save" state over the user's allocations
  const previewConflicts = useMemo(() => {
    if (!form.start_date || !form.end_date || !form.project_id) {
      return [];
    }
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return [];
    }
    const simulated = [
      ...allocations.filter((a) => a.id !== editingId),
      {
        id: editingId || 'draft',
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        allocation_percent: Number(form.allocation_percent) || 0,
        project_id: form.project_id,
      },
    ];
    const conflicts = [];
    simulated.forEach((a, i) => {
      for (let j = i + 1; j < simulated.length; j += 1) {
        const b = simulated[j];
        const s = new Date(Math.max(new Date(a.start_date), new Date(b.start_date)));
        const e = new Date(Math.min(new Date(a.end_date), new Date(b.end_date)));
        if (s <= e) {
          const total =
            Number(a.allocation_percent || 0) + Number(b.allocation_percent || 0);
          const overlapsDraft = a.id === 'draft' || b.id === 'draft';
          if (overlapsDraft) {
            conflicts.push({
              otherId: a.id === 'draft' ? b.id : a.id,
              start: s,
              end: e,
              total,
              over: total > 100,
            });
          }
        }
      }
    });
    return conflicts;
  }, [form, allocations, editingId]);

  if (!user) return null;

  const title = `Allocations for ${displayName(user)}`;

  const refetchAllocations = async () => {
    try {
      const res = await axios.get('/api/resource-utilization/allocations', {
        params: { user_id: user.id },
      });
      setAllocations(res.data?.allocations || []);
    } catch (err) {
      // non-fatal; keep the local state
    }
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, project_id: initialProjectId || '' });
    setEditingId(null);
  };

  const handleEdit = (allocation) => {
    setEditingId(allocation.id);
    setForm({
      project_id: allocation.project_id || '',
      phase: allocation.phase || '',
      start_date: toInputDate(allocation.start_date),
      end_date: toInputDate(allocation.end_date),
      allocation_percent: allocation.allocation_percent ?? 100,
      is_billable: allocation.is_billable ?? null,
    });
  };

  const handleDelete = async (allocation) => {
    if (!window.confirm('Delete this allocation?')) return;
    try {
      const res = await axios.delete(
        `/api/resource-utilization/allocations/${allocation.id}`,
      );
      setConflictReport(res.data?.conflict_report || null);
      toast.success('Allocation deleted');
      await refetchAllocations();
      onSaved?.(user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) {
      toast.error('Pick a project');
      return;
    }
    const startIso = toIsoAtMidnight(form.start_date);
    const endIso = toIsoAtMidnight(form.end_date);
    if (!startIso || !endIso) {
      toast.error('Pick valid dates');
      return;
    }
    if (new Date(endIso) < new Date(startIso)) {
      toast.error('End date must be on or after start date');
      return;
    }
    const pct = Number(form.allocation_percent);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Allocation % must be 0-100');
      return;
    }

    const payload = {
      phase: form.phase?.trim() || null,
      start_date: startIso,
      end_date: endIso,
      allocation_percent: pct,
      is_billable:
        form.is_billable === null || form.is_billable === ''
          ? null
          : Boolean(form.is_billable),
    };

    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.patch(
          `/api/resource-utilization/allocations/${editingId}`,
          payload,
        );
        setConflictReport(res.data?.conflict_report || null);
        toast.success('Allocation updated');
      } else {
        const res = await axios.post('/api/resource-utilization/allocations', {
          ...payload,
          user_id: user.id,
          project_id: form.project_id,
        });
        setConflictReport(res.data?.conflict_report || null);
        toast.success('Allocation created');
      }
      resetForm();
      await refetchAllocations();
      onSaved?.(user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle="Plan deployment windows and surface over-allocation conflicts."
      icon={<CalendarPlus className="h-5 w-5" />}
      widthClass="sm:max-w-xl lg:max-w-3xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md text-sm border border-secondary-200 dark:border-secondary-600 text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700"
          >
            Done
          </button>
        </div>
      }
    >
      <div className="space-y-6" data-testid="allocation-drawer">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400 mb-2">
            Current allocations
          </h3>
          {allocations.length === 0 ? (
            <EmptyState
              icon={CalendarPlus}
              title="No allocations yet"
              description="Create the first deployment window below."
            />
          ) : (
            <ul className="divide-y divide-secondary-100 dark:divide-secondary-700/80 border border-secondary-200 dark:border-secondary-700 rounded-md overflow-hidden">
              {allocations.map((a) => (
                <li
                  key={a.id}
                  data-testid={`allocation-row-${a.id}`}
                  className="p-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                      {a.project_name || 'Project'}
                      {a.phase ? ` • ${a.phase}` : ''}
                    </div>
                    <div className="text-xs text-secondary-500 dark:text-secondary-400">
                      {formatDate(a.start_date)} → {formatDate(a.end_date)} •{' '}
                      {a.allocation_percent}% •{' '}
                      {a.is_billable ? 'Billable' : 'Non-billable'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.lifecycle && (
                      <StatusPill
                        status={lifecycleLabel(a.lifecycle)}
                        colorClass={lifecyclePillClass(a.lifecycle)}
                        showDot={false}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => handleEdit(a)}
                      className="p-1.5 rounded-md text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                      aria-label="Edit allocation"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(a)}
                      className="p-1.5 rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      aria-label="Delete allocation"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
              {editingId ? 'Edit allocation' : 'Add allocation'}
            </h3>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-700"
              >
                <XIcon className="h-3 w-3" />
                Cancel edit
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="allocation-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Project
                </label>
                <ProjectCombobox
                  value={form.project_id}
                  onChange={(id) =>
                    setForm((f) => ({ ...f, project_id: id }))
                  }
                  projects={scopedProjects}
                  disabled={!!editingId}
                  emptyLabel={
                    user
                      ? `${displayName(user)} is not assigned to any project yet`
                      : 'No projects available'
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Phase / role
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g. UI/UX, Engineering, DevOps"
                  value={form.phase}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phase: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Start date
                </label>
                <input
                  type="date"
                  className="input-field w-full"
                  value={form.start_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, start_date: e.target.value }))
                  }
                  data-testid="allocation-start"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  End date
                </label>
                <input
                  type="date"
                  className="input-field w-full"
                  value={form.end_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, end_date: e.target.value }))
                  }
                  data-testid="allocation-end"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Allocation %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  className="input-field w-full"
                  value={form.allocation_percent}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      allocation_percent: e.target.value,
                    }))
                  }
                  data-testid="allocation-percent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Billable override
                </label>
                <select
                  className="input-field w-full"
                  value={
                    form.is_billable === null
                      ? ''
                      : form.is_billable
                      ? 'true'
                      : 'false'
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      is_billable: v === '' ? null : v === 'true',
                    }));
                  }}
                >
                  <option value="">Inherit from project</option>
                  <option value="true">Billable</option>
                  <option value="false">Non-billable</option>
                </select>
              </div>
            </div>

            {previewConflicts.length > 0 && (
              <div
                data-testid="allocation-conflict-preview"
                className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-xs text-amber-800 dark:text-amber-200"
              >
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {previewConflicts.some((c) => c.over)
                    ? 'This would over-allocate the employee'
                    : 'This would overlap another allocation'}
                </div>
                <ul className="space-y-0.5">
                  {previewConflicts.map((c, idx) => {
                    const other = allocations.find((a) => a.id === c.otherId);
                    return (
                      <li key={`${c.otherId}-${idx}`}>
                        Overlaps {other?.project_name || 'another allocation'}:{' '}
                        {formatDate(c.start)} → {formatDate(c.end)}
                        {c.over ? ` (total ${c.total}% > 100%)` : ''}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                data-testid="allocation-submit"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add allocation'}
              </button>
            </div>
          </form>
        </section>

        {conflictReport && (conflictReport.is_shared || conflictReport.is_over_allocated) && (
          <section
            data-testid="allocation-conflict-report"
            className="rounded-md border border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-xs text-red-800 dark:text-red-200"
          >
            <div className="font-semibold mb-1 inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {conflictReport.is_over_allocated
                ? 'Employee is over-allocated'
                : 'Employee is shared across projects'}
            </div>
            <ul className="space-y-0.5">
              {(conflictReport.conflicts || []).map((c, idx) => (
                <li key={idx}>
                  {formatDate(c.start)} → {formatDate(c.end)}: total{' '}
                  {c.total_percent}% across {c.allocation_ids?.length || 0}{' '}
                  allocations
                  {c.over_allocated ? ' (over 100%)' : ''}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </FormDrawer>
  );
};

export default AllocationDrawer;
